import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Chip,
  Stack,
  ListItemButton,
} from '@mui/material';
import {
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { WidgetCard } from './WidgetCard';
import { WidgetItemsModal } from './WidgetItemsModal';
import { MoreItemsBadge } from './MoreItemsBadge';
import { useOverdueTasks } from '../../../hooks/useTasks';
import { useTaskMutations } from '../../../hooks/useTaskMutations';
import { TaskPriorityBadge } from '../../tasks/TaskPriorityBadge';
import type { TaskWithClient } from '../../../types/task';

const MAX_VISIBLE_ITEMS = 3;

export const OverdueTasksWidget: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const { data, isLoading, error, refetch } = useOverdueTasks();
  const { completeTask } = useTaskMutations();
  const navigate = useNavigate();

  const tasks = data?.data?.tasks || [];
  const visibleTasks = tasks.slice(0, MAX_VISIBLE_ITEMS);
  const remainingCount = Math.max(0, tasks.length - MAX_VISIBLE_ITEMS);

  const handleComplete = async (task: TaskWithClient, e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await completeTask.mutateAsync(task._id);
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const handleTaskClick = (taskId: string) => {
    setModalOpen(false);
    navigate(`/tasks?id=${taskId}`);
  };

  const getOverdueLabel = (dueDate: string) => {
    return formatDistanceToNow(new Date(dueDate), { addSuffix: true });
  };

  const TaskListItem = ({ task, inModal = false }: { task: TaskWithClient; inModal?: boolean }) => (
    <ListItem
      disablePadding
      sx={{
        borderLeft: '3px solid',
        borderColor: 'error.main',
        mb: 0.5,
        borderRadius: 1,
        bgcolor: 'error.light',
        opacity: 0.9,
      }}
    >
      <ListItemButton
        onClick={() => handleTaskClick(task._id)}
        sx={{
          py: inModal ? 1.5 : 0.5,
          pl: 1,
          pr: inModal ? 2 : 1,
          '&:hover': {
            backgroundColor: 'error.light',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Checkbox
            size="small"
            checked={false}
            onClick={(e) => {
              e.stopPropagation();
              handleComplete(task);
            }}
          />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {task.title}
            </Typography>
          }
          secondary={
            <Stack direction="row" spacing={0.5} mt={0.25} alignItems="center">
              <TaskPriorityBadge priority={task.priority} size="small" showIcon={false} />
              {task.dueDate && (
                <Chip
                  label={getOverdueLabel(task.dueDate)}
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Stack>
          }
        />
      </ListItemButton>
    </ListItem>
  );

  const EmptyState = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100%"
      py={2}
    >
      <CheckCircleIcon sx={{ fontSize: 48, color: 'success.light', mb: 1 }} />
      <Typography variant="body2" color="text.secondary">
        No overdue tasks
      </Typography>
      <Typography variant="caption" color="text.secondary">
        You're all caught up!
      </Typography>
    </Box>
  );

  return (
    <>
      <WidgetCard
        title="Overdue Tasks"
        icon={<WarningIcon color="error" />}
        isLoading={isLoading}
        error={error as Error | null}
        onRefresh={() => refetch()}
        viewAllLink="/tasks?overdue=true"
        viewAllLabel="View Overdue"
        minHeight={180}
        action={
          tasks.length > 0 ? (
            <Chip
              label={tasks.length}
              color="error"
              size="small"
              sx={{ fontWeight: 'bold' }}
            />
          ) : undefined
        }
      >
        {tasks.length === 0 ? (
          <EmptyState />
        ) : (
          <Box display="flex" flexDirection="column" height="100%">
            <List dense disablePadding sx={{ flex: 1 }}>
              {visibleTasks.map((task) => (
                <TaskListItem key={task._id} task={task} />
              ))}
            </List>
            {remainingCount > 0 && (
              <Box display="flex" justifyContent="center" pt={1}>
                <MoreItemsBadge
                  count={remainingCount}
                  onClick={() => setModalOpen(true)}
                  color="error"
                  label={`+${remainingCount} more overdue tasks. Click to view all`}
                />
              </Box>
            )}
          </Box>
        )}
      </WidgetCard>

      <WidgetItemsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Overdue Tasks"
        icon={<WarningIcon color="error" />}
        totalCount={tasks.length}
      >
        {tasks.map((task) => (
          <TaskListItem key={task._id} task={task} inModal />
        ))}
      </WidgetItemsModal>
    </>
  );
};

export default OverdueTasksWidget;
